import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { OptimizedImage } from "@/components/ui/optimized-image";
import type { Category } from "@/lib/types";

interface CategoryCardProps {
  category: Category;
  brandCount?: number;
}

function getCategoryColor(slug: string): string {
  return `var(--cat-${slug}, var(--accent))`;
}

export function CategoryCard({ category, brandCount }: CategoryCardProps) {
  const catColor = getCategoryColor(category.slug);

  return (
    <Link href={`/categories/${category.slug}`}>
      <Card className="h-full hover:shadow-lg hover:-translate-y-1 transition-all group border-border p-0 overflow-hidden">
        <div className="relative h-40 w-full overflow-hidden bg-muted">
          {category.image && (
            <OptimizedImage
              src={category.image}
              alt={category.name}
              fallbackSrc="/placeholder-category.jpg"
              fill
              className="group-hover:scale-105 transition-transform"
              sizes="(max-width: 640px) 90vw, (max-width: 1024px) 45vw, (max-width: 1280px) 30vw, 20vw"
              quality={60}
              objectFit="cover"
            />
          )}
          <div
            className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity"
            style={{ background: `linear-gradient(135deg, ${catColor}, transparent 60%)` }}
          />
        </div>
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-xl text-foreground group-hover:text-accent transition-colors">
                {category.name}
              </h3>
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-accent transition-colors flex-shrink-0 mt-1" />
          </div>
          <p className="mt-3 text-sm text-muted-foreground line-clamp-3 leading-relaxed">
            {category.description}
          </p>
          <div className="mt-6 pt-4 border-t border-border flex items-center gap-2">
            <span
              className="inline-block h-2 w-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: catColor }}
            />
            <p className="text-sm font-medium" style={{ color: catColor }}>
              {brandCount ?? category.brandSlugs?.length ?? 0} brand
              {(brandCount ?? category.brandSlugs?.length ?? 0) !== 1 ? "s" : ""}
            </p>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
